import { useEffect, useRef } from 'react';
import { drawPolicyNewsWidget, drawTimeWidget, drawWeatherWidget, isLocalTimeSource, isNewsListSource, preloadWidgetFonts, type Notice, type NoticeDataWidgetLayer, type NoticeWidgetData } from '@cornerstone/shared';
import { newsListSample } from './PolicyNewsWidgetPreview';

type Draft = Pick<Notice, 'canvasWidth' | 'canvasHeight' | 'canvasFill' | 'canvasFillOpacity' | 'layers'>;
/** Gallery-only sample so the weather icon has something to show before the device fetches real data. */
const SAMPLE_WEATHER: NoticeWidgetData = { status: 'ready', title: '날씨', lines: [], sourceLabel: '미리보기', updatedAt: null,
  weather: { placeName: '설치 지역', temperatureC: 24, humidityPercent: 55, windKmh: 9, code: 0 } };

/** One data widget drawn at its own size with gallery sample data (used inside SVG template previews). */
export function DataWidgetLayerCanvas({ layer, backdrop }: { layer: NoticeDataWidgetLayer; backdrop: string }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const node = canvas.current, ctx = node?.getContext('2d');
    if (!node || !ctx) return;
    preloadWidgetFonts([layer]);
    const scale = Math.min(1, 1024 / Math.max(layer.width, layer.height));
    node.width = Math.max(1, Math.round(layer.width * scale)); node.height = Math.max(1, Math.round(layer.height * scale));
    ctx.scale(scale, scale);
    const local = { ...layer, x: 0, y: 0 };
    if (isLocalTimeSource(local.source)) drawTimeWidget(ctx, local);
    else if (local.source.kind === 'weather') drawWeatherWidget(ctx, local, SAMPLE_WEATHER, backdrop);
    else if (isNewsListSource(local.source)) drawPolicyNewsWidget(ctx, local, newsListSample(local));
  }, [layer, backdrop]);
  return <canvas ref={canvas} aria-hidden="true" style={{ width: '100%', height: '100%', display: 'block', pointerEvents: 'none' }} />;
}

/** Live canvas preview for widget templates: text/panel layers plus clock, date, weekday and
 * weather widgets drawn with the shared renderers, ticking so the seconds tape actually moves. */
export function WidgetTemplatePreview({ draft, label, maxPixels = 720 }: { draft: Draft; label: string; maxPixels?: number }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const node = canvas.current, ctx = node?.getContext('2d');
    if (!node || !ctx) return;
    preloadWidgetFonts(draft.layers);
    const scale = Math.min(1, maxPixels / Math.max(draft.canvasWidth, draft.canvasHeight));
    node.width = Math.max(1, Math.round(draft.canvasWidth * scale)); node.height = Math.max(1, Math.round(draft.canvasHeight * scale));
    let raf = 0, active = true;
    const tick = () => {
      if (!active) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, node.width, node.height);
      ctx.globalAlpha = Math.max(0, Math.min(1, (draft.canvasFillOpacity ?? 100) / 100));
      ctx.fillStyle = draft.canvasFill ?? '#000'; ctx.fillRect(0, 0, node.width, node.height);
      ctx.globalAlpha = 1; ctx.scale(scale, scale);
      for (const layer of [...draft.layers].sort((a, b) => a.z - b.z)) {
        if (layer.hidden) continue;
        ctx.save(); ctx.globalAlpha = layer.opacity;
        if (layer.kind === 'dataWidget' && isLocalTimeSource(layer.source)) drawTimeWidget(ctx, layer);
        else if (layer.kind === 'dataWidget' && layer.source.kind === 'weather') drawWeatherWidget(ctx, layer, SAMPLE_WEATHER, draft.canvasFill ?? '#000');
        else if (layer.kind === 'text') {
          if (layer.backgroundColor) { ctx.fillStyle = layer.backgroundColor; ctx.fillRect(layer.x, layer.y, layer.width, layer.height); }
          if (layer.text) {
            ctx.fillStyle = layer.color; ctx.font = `${layer.fontWeight} ${layer.fontSize}px ${layer.fontFamily ? `'${layer.fontFamily}', ` : ''}sans-serif`;
            ctx.textAlign = layer.align; ctx.textBaseline = 'middle';
            const x = layer.align === 'left' ? layer.x + layer.paddingX : layer.align === 'right' ? layer.x + layer.width - layer.paddingX : layer.x + layer.width / 2;
            layer.text.split('\n').forEach((line, index, lines) => ctx.fillText(line, x, layer.y + layer.height / 2 + (index - (lines.length - 1) / 2) * layer.fontSize * 1.2));
          }
        }
        ctx.restore();
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => { active = false; cancelAnimationFrame(raf); };
  }, [draft, maxPixels]);
  return <canvas ref={canvas} role="img" aria-label={label} style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain', pointerEvents: 'none' }} />;
}
